import { useEffect, useState } from "react";
import { Download, Server, Wifi, Gauge, ShieldCheck, PlugZap, Terminal, ExternalLink, AlertTriangle, HelpCircle, Pencil, Save, X, Plus, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, SectionCard } from "@/components/common";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

const ICONS = { Download, Server, Wifi, Gauge, ShieldCheck, PlugZap, Terminal };

// Built-in default guide. The admin can override any text via the editor; the
// saved copy is stored on the backend and merged over this on load.
const DEFAULT_GUIDE = {
  overview: {
    title: "How it works",
    body: "SimHub runs on each sim PC and reads telemetry from the racing/flight title in real time. A lightweight connector on the same PC forwards that telemetry to the Pit Lane Lounge backend, which then shows the live driver stats on the app (leaderboards, session summaries, tournaments).",
  },
  steps: [
    {
      icon: "Download", title: "Install SimHub on the simulator PC",
      blocks: [
        { t: "p", text: "Download the latest SimHub build from the official site and install it on every sim PC:" },
        { t: "link", label: "Open simhubdash.com/downloads", url: "https://www.simhubdash.com/download-2/" },
        { t: "bullets", items: [
          "Windows 10/11 recommended. Left the sim rig running headless? Enable auto-login on Windows and add SimHub to Startup.",
          "During install accept the default install path (e.g. C:\\Program Files (x86)\\SimHub).",
          "Launch SimHub once and let it index installed racing titles.",
        ] },
      ],
    },
    {
      icon: "Gauge", title: "Enable telemetry for your title",
      blocks: [
        { t: "p", text: "Inside SimHub, open the \"Games\" tab and enable the plugin for each title you offer. Common ones:" },
        { t: "bullets", items: [
          "Assetto Corsa / ACC — enable the SimHub plugin from the Games tab.",
          "iRacing — install the SimHub iRacing plugin (SimHub prompts you).",
          "F1 24 / F1 25 — SimHub reads UDP telemetry; make sure UDP output is ON inside the game.",
          "rFactor 2, Automobilista 2, RaceRoom — enable via Games tab.",
          "MSFS 2024 (flight) — install the SimHub MSFS plugin.",
        ] },
        { t: "callout", tone: "warn", text: "After enabling a plugin you must start the game AT LEAST once so SimHub can register itself with the sim." },
      ],
    },
    {
      icon: "Server", title: "Turn on the SimHub web server",
      blocks: [
        { t: "p", text: "Give the connector a local endpoint to read from:" },
        { t: "bullets", items: [
          "In SimHub open Dash Studio (left sidebar), then click the gear icon to open Dashboard Server settings.",
          "Check \"Enable dashboard server\". The default port is 8888 (keep it consistent across all rigs).",
          "Leave \"Allow connections from any IP\" OFF — the connector runs on the same PC and reads 127.0.0.1.",
          "Save, then confirm it works by opening http://127.0.0.1:8888/api/getgamedata in a browser on the sim PC while a game is running — you should see a JSON payload.",
        ] },
      ],
    },
    {
      icon: "Terminal", title: "Install Python & the connector script",
      blocks: [
        { t: "p", text: "The connector is a tiny Python script (simhub_bridge/connector.py) that polls SimHub's local web server and forwards telemetry to the backend. It runs on the sim PC — no separate machine, no installer." },
        { t: "bullets", items: [
          "Install Python 3.12+ from python.org. On the FIRST install screen, tick \"Add python.exe to PATH\".",
          "Make a folder on the sim PC, e.g. C:\\PitLane\\ , and save two files into it: connector.py and config.json (from simhub_bridge/ in your project, or ask us for the copy-paste).",
        ] },
        { t: "p", text: "Open a command window in that folder: in File Explorer, click the address bar, type cmd, press Enter. Then run:" },
        { t: "cmd", text: "py --version" },
        { t: "cmd", text: "py -m pip install requests" },
        { t: "callout", tone: "info", text: "Always use py -m pip (not bare pip) so the library installs into the same Python that runs the script. If py isn't recognized, Python wasn't added to PATH — reinstall and tick that box." },
      ],
    },
    {
      icon: "ShieldCheck", title: "Get the Station API key & fill config.json",
      blocks: [
        { t: "p", text: "Each sim PC authenticates to the backend with a per-station API key:" },
        { t: "bullets", items: [
          "In the admin console open Stations → this sim → edit → enable \"SimHub telemetry\" to generate its Station API key, then copy it.",
          "Open config.json in Notepad and paste the key as \"station_key\". Set \"backend_url\" to your LIVE app domain. Leave \"simhub_url\" as http://127.0.0.1:8888/api/getgamedata.",
          "Save As → set Encoding to \"UTF-8\" (not \"UTF-8 with BOM\") to avoid a JSON error. Keep config.json on the PC only; rotate the key anytime via Stations → Regenerate.",
        ] },
        { t: "cmd", text: "{\n  \"backend_url\": \"https://your-live-app-domain\",\n  \"station_key\": \"PASTE_STATION_API_KEY\",\n  \"simhub_url\": \"http://127.0.0.1:8888/api/getgamedata\",\n  \"poll_seconds\": 1.0\n}" },
      ],
    },
    {
      icon: "PlugZap", title: "Run the connector",
      blocks: [
        { t: "p", text: "In the same command window (still inside C:\\PitLane), run:" },
        { t: "cmd", text: "py connector.py" },
        { t: "p", text: "Success looks like one line, then it sits quietly running — leave the window open (closing it stops the connector):" },
        { t: "cmd", text: "[connector] http://127.0.0.1:8888/api/getgamedata -> https://your-domain/api/simhub/ingest" },
        { t: "bullets", items: [
          "To auto-start on boot: press Win+R, type shell:startup, Enter. Create PitLane.bat in that folder containing two lines: cd C:\\PitLane   and   py connector.py",
        ] },
      ],
    },
    {
      icon: "Wifi", title: "Verify live telemetry",
      blocks: [
        { t: "p", text: "Confirm the pipeline end-to-end:" },
        { t: "bullets", items: [
          "Launch a SimHub-supported game on that PC and get on track. Check http://127.0.0.1:8888/api/getgamedata in a browser shows REAL values (not \"NewData\": null).",
          "A customer scans the station's QR → pairs to that station. Drive a lap.",
          "In Admin → Sim Sessions you should see the active session and lap times within a few seconds; the station tile shows a green \"LIVE\" badge.",
        ] },
        { t: "callout", tone: "ok", text: "Once the LIVE badge shows for every rig, you're done. Tournaments and per-session summaries collect automatically." },
      ],
    },
  ],
  troubleshooting: [
    { issue: "ModuleNotFoundError: No module named 'requests'", fix: "Install it into the SAME Python that runs the script: py -m pip install requests (then confirm with py -m pip show requests). Avoid bare 'pip' — it can target a different Python install." },
    { issue: "json ... Unexpected UTF-8 BOM", fix: "Notepad saved config.json with a BOM. Re-save via File → Save As → Encoding 'UTF-8' (not 'UTF-8 with BOM'). The latest connector.py opens config with utf-8-sig, which also fixes this." },
    { issue: "[connector] error: 'NoneType' object has no attribute 'get' (repeating)", fix: "Normal when no game is running — SimHub returns NewData: null while idle. The latest connector.py skips idle polls quietly. Launch a game and get on track; the errors stop and telemetry flows." },
    { issue: "Lap times don't appear / station shows OFFLINE.", fix: "Enable SimHub's dashboard server (Dash Studio → gear → Enable dashboard server, port 8888) and confirm http://127.0.0.1:8888/api/getgamedata returns JSON on the sim PC. Ensure connector.py is running and the game's plugin is enabled in SimHub." },
    { issue: "Telemetry never reaches the app even though SimHub reads fine.", fix: "Check config.json backend_url is your LIVE deployed domain and is reachable from the sim PC (open https://your-domain/api/simhub/active in a browser there). If it errors, fix the URL. Allow python.exe outbound on port 443 if a firewall blocks it." },
  ],
  links: [
    { label: "SimHub official downloads", url: "https://www.simhubdash.com/download-2/" },
    { label: "SimHub wiki (games, plugins, API)", url: "https://github.com/SHWotever/SimHub/wiki" },
    { label: "SimHub Discord community", url: "https://discord.gg/simhub" },
  ],
};

const clone = (o) => JSON.parse(JSON.stringify(o));

export default function AdminSimHub() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [guide, setGuide] = useState(DEFAULT_GUIDE);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const downloadConnector = async () => {
    setDownloading(true);
    try {
      const res = await apiClient.get("/admin/simhub/connector.zip", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = "PitLane-SimHub-Connector.zip";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(getErrorMessage(e, "Download failed")); }
    finally { setDownloading(false); }
  };

  useEffect(() => {
    apiClient.get("/admin/simhub/guide")
      .then(({ data }) => { if (data && data.steps && data.steps.length) setGuide(data); })
      .catch(() => { /* keep default */ });
  }, []);

  const startEdit = () => { setDraft(clone(guide)); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setDraft(null); };
  const resetDefault = () => { setDraft(clone(DEFAULT_GUIDE)); toast.info("Loaded the built-in default — remember to Save."); };

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put("/admin/simhub/guide", draft);
      setGuide(draft); setEditing(false); setDraft(null);
      toast.success("SimHub guide saved");
    } catch (e) { toast.error(getErrorMessage(e, "Could not save (admin only)")); }
    finally { setSaving(false); }
  };

  const g = editing ? draft : guide;

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <PageHeader
          title="SimHub Integration"
          subtitle="Wire up each simulator PC to feed live telemetry (speed, gear, laps, lap times) into the lounge app."
        />
        <div className="flex gap-2 pt-1">
          {!editing && (
            <button onClick={downloadConnector} disabled={downloading} className="neon-outline-btn px-4 py-2 text-xs" data-testid="simhub-download-btn"><Download className="w-3.5 h-3.5" /> {downloading ? "Preparing…" : "Download connector"}</button>
          )}
          {!editing ? (
            isAdmin && <button onClick={startEdit} className="neon-outline-btn px-4 py-2 text-xs" data-testid="simhub-edit-btn"><Pencil className="w-3.5 h-3.5" /> Edit guide</button>
          ) : (
            <>
              <button onClick={resetDefault} className="neon-outline-btn px-3 py-2 text-xs" data-testid="simhub-reset-btn"><RotateCcw className="w-3.5 h-3.5" /> Reset to default</button>
              <button onClick={cancelEdit} className="neon-outline-btn px-3 py-2 text-xs" data-testid="simhub-cancel-btn"><X className="w-3.5 h-3.5" /> Cancel</button>
              <button onClick={save} disabled={saving} className="neon-btn px-4 py-2 text-xs" data-testid="simhub-save-btn"><Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}</button>
            </>
          )}
        </div>
      </div>

      {editing
        ? <GuideEditor draft={draft} setDraft={setDraft} />
        : <GuideView guide={g} />}
    </div>
  );
}

/* ---------------- Read-only render ---------------- */
function GuideView({ guide }) {
  return (
    <>
      <SectionCard className="mb-6" data-testid="simhub-overview">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <h3 className="font-display text-lg font-bold text-white uppercase tracking-wider mb-2 flex items-center gap-2">
              <Gauge className="w-5 h-5" style={{ color: "var(--neon-cyan)" }} /> {guide.overview.title}
            </h3>
            <p className="text-slate-300 text-sm leading-relaxed">{guide.overview.body}</p>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <MiniBadge label="Sim PC" icon={Server} />
            <MiniBadge label="Connector" icon={PlugZap} />
            <MiniBadge label="Backend" icon={Wifi} />
          </div>
        </div>
      </SectionCard>

      {guide.steps.map((step, i) => {
        const Icon = ICONS[step.icon] || Server;
        return (
          <Step key={i} number={i + 1} title={step.title} icon={Icon} testId={`step-${i + 1}`}>
            {step.blocks.map((b, j) => <Block key={j} b={b} />)}
          </Step>
        );
      })}

      <SectionCard className="mt-6" data-testid="simhub-troubleshoot">
        <h3 className="font-display text-lg font-bold text-white uppercase tracking-wider mb-3 flex items-center gap-2">
          <HelpCircle className="w-5 h-5" style={{ color: "#ffb020" }} /> Troubleshooting
        </h3>
        {guide.troubleshooting.map((r, i) => <TroubleRow key={i} issue={r.issue} fix={r.fix} />)}
      </SectionCard>

      <SectionCard className="mt-6" data-testid="simhub-links">
        <h3 className="font-display text-lg font-bold text-white uppercase tracking-wider mb-3 flex items-center gap-2">
          <ExternalLink className="w-5 h-5" style={{ color: "var(--neon-cyan)" }} /> Reference links
        </h3>
        <ul className="space-y-2 text-sm">
          {guide.links.map((l, i) => (
            <li key={i}><a href={l.url} target="_blank" rel="noreferrer" className="text-[var(--neon-cyan)] hover:underline">{l.label}</a></li>
          ))}
        </ul>
      </SectionCard>
    </>
  );
}

function Block({ b }) {
  if (b.t === "p") return <p>{b.text}</p>;
  if (b.t === "link") return (
    <a href={b.url} target="_blank" rel="noreferrer" className="neon-outline-btn px-4 py-2 text-xs my-3 inline-flex"><ExternalLink className="w-3.5 h-3.5" /> {b.label}</a>
  );
  if (b.t === "bullets") return <Bullets items={b.items} />;
  if (b.t === "cmd") return <Cmd>{b.text}</Cmd>;
  if (b.t === "callout") return <Callout tone={b.tone} icon={b.tone === "warn" ? AlertTriangle : b.tone === "ok" ? ShieldCheck : Terminal}>{b.text}</Callout>;
  return null;
}

/* ---------------- Editor ---------------- */
function GuideEditor({ draft, setDraft }) {
  const update = (fn) => { const d = clone(draft); fn(d); setDraft(d); };

  return (
    <div className="space-y-4" data-testid="simhub-editor">
      <Callout tone="info" icon={Pencil}>
        Edit any text below. The layout, icons and step order stay fixed — only the wording changes. Use the Save button when done.
      </Callout>

      <SectionCard>
        <FieldLabel>Overview title</FieldLabel>
        <TextInput value={draft.overview.title} onChange={(v) => update((d) => { d.overview.title = v; })} testId="edit-overview-title" />
        <FieldLabel className="mt-3">Overview body</FieldLabel>
        <TextArea value={draft.overview.body} onChange={(v) => update((d) => { d.overview.body = v; })} testId="edit-overview-body" />
      </SectionCard>

      {draft.steps.map((step, si) => (
        <SectionCard key={si} data-testid={`edit-step-${si + 1}`}>
          <div className="text-xs font-bold uppercase tracking-widest text-[var(--neon-cyan)] mb-2">Step {si + 1}</div>
          <FieldLabel>Title</FieldLabel>
          <TextInput value={step.title} onChange={(v) => update((d) => { d.steps[si].title = v; })} />
          {step.blocks.map((b, bi) => (
            <div key={bi} className="mt-3 border-l-2 border-white/10 pl-3">
              {b.t === "p" && (<><FieldLabel>Paragraph</FieldLabel>
                <TextArea value={b.text} onChange={(v) => update((d) => { d.steps[si].blocks[bi].text = v; })} /></>)}
              {b.t === "cmd" && (<><FieldLabel>Command / code</FieldLabel>
                <TextArea mono value={b.text} onChange={(v) => update((d) => { d.steps[si].blocks[bi].text = v; })} /></>)}
              {b.t === "callout" && (<><FieldLabel>Callout ({b.tone})</FieldLabel>
                <TextArea value={b.text} onChange={(v) => update((d) => { d.steps[si].blocks[bi].text = v; })} /></>)}
              {b.t === "link" && (<div className="grid grid-cols-2 gap-2">
                <div><FieldLabel>Link label</FieldLabel><TextInput value={b.label} onChange={(v) => update((d) => { d.steps[si].blocks[bi].label = v; })} /></div>
                <div><FieldLabel>Link URL</FieldLabel><TextInput value={b.url} onChange={(v) => update((d) => { d.steps[si].blocks[bi].url = v; })} /></div>
              </div>)}
              {b.t === "bullets" && (<>
                <FieldLabel>Bullet lines</FieldLabel>
                <LineList
                  items={b.items}
                  onChange={(items) => update((d) => { d.steps[si].blocks[bi].items = items; })}
                />
              </>)}
            </div>
          ))}
        </SectionCard>
      ))}

      <SectionCard>
        <FieldLabel>Troubleshooting rows</FieldLabel>
        {draft.troubleshooting.map((r, i) => (
          <div key={i} className="mt-2 grid gap-1 border-l-2 border-white/10 pl-3">
            <div className="flex items-center gap-2">
              <TextInput value={r.issue} onChange={(v) => update((d) => { d.troubleshooting[i].issue = v; })} placeholder="Issue" />
              <IconBtn onClick={() => update((d) => { d.troubleshooting.splice(i, 1); })}><Trash2 className="w-4 h-4" /></IconBtn>
            </div>
            <TextArea value={r.fix} onChange={(v) => update((d) => { d.troubleshooting[i].fix = v; })} placeholder="Fix" />
          </div>
        ))}
        <AddBtn onClick={() => update((d) => { d.troubleshooting.push({ issue: "New issue", fix: "How to fix it" }); })}>Add row</AddBtn>
      </SectionCard>

      <SectionCard>
        <FieldLabel>Reference links</FieldLabel>
        {draft.links.map((l, i) => (
          <div key={i} className="mt-2 flex items-center gap-2">
            <TextInput value={l.label} onChange={(v) => update((d) => { d.links[i].label = v; })} placeholder="Label" />
            <TextInput value={l.url} onChange={(v) => update((d) => { d.links[i].url = v; })} placeholder="https://…" />
            <IconBtn onClick={() => update((d) => { d.links.splice(i, 1); })}><Trash2 className="w-4 h-4" /></IconBtn>
          </div>
        ))}
        <AddBtn onClick={() => update((d) => { d.links.push({ label: "New link", url: "https://" }); })}>Add link</AddBtn>
      </SectionCard>
    </div>
  );
}

function LineList({ items, onChange }) {
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          <TextInput value={it} onChange={(v) => { const n = [...items]; n[i] = v; onChange(n); }} />
          <IconBtn onClick={() => { const n = items.filter((_, k) => k !== i); onChange(n); }}><Trash2 className="w-4 h-4" /></IconBtn>
        </div>
      ))}
      <AddBtn onClick={() => onChange([...items, "New line"])}>Add line</AddBtn>
    </div>
  );
}

/* ---------------- Small UI helpers ---------------- */
function FieldLabel({ children, className = "" }) {
  return <label className={`block text-slate-400 text-xs uppercase tracking-wider mb-1 ${className}`}>{children}</label>;
}
function TextInput({ value, onChange, placeholder, testId }) {
  return <input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testId}
    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-cyan-400" />;
}
function TextArea({ value, onChange, placeholder, mono, testId }) {
  return <textarea value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} rows={mono ? 4 : 3} data-testid={testId}
    className={`w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-cyan-400 ${mono ? "font-mono text-xs text-[var(--neon-cyan)]" : ""}`} />;
}
function IconBtn({ children, onClick }) {
  return <button onClick={onClick} className="shrink-0 p-2 rounded-lg border border-white/10 text-slate-400 hover:text-pink-400 hover:border-pink-400/40">{children}</button>;
}
function AddBtn({ children, onClick }) {
  return <button onClick={onClick} className="mt-2 inline-flex items-center gap-1.5 text-cyan-400 text-xs"><Plus className="w-3.5 h-3.5" /> {children}</button>;
}

function Step({ number, title, icon: Icon, children, testId }) {
  return (
    <SectionCard className="mb-4 relative" data-testid={testId}>
      <div className="absolute top-6 left-6 w-9 h-9 rounded-full grid place-items-center font-display font-black"
        style={{ background: "var(--neon-cyan)", color: "#05060d" }}>
        {number}
      </div>
      <div className="pl-14">
        <h3 className="font-display text-lg font-bold text-white uppercase tracking-wider mb-3 flex items-center gap-2">
          <Icon className="w-5 h-5" style={{ color: "var(--neon-cyan)" }} /> {title}
        </h3>
        <div className="text-slate-300 text-sm space-y-2">{children}</div>
      </div>
    </SectionCard>
  );
}

function Bullets({ items }) {
  return (
    <ul className="list-disc pl-5 space-y-1.5 text-slate-300 text-sm">
      {items.map((item, i) => <li key={i}>{item}</li>)}
    </ul>
  );
}

function Cmd({ children }) {
  return (
    <pre className="my-2 rounded-lg border border-white/10 bg-black/50 px-3 py-2 overflow-x-auto text-xs leading-relaxed text-[var(--neon-cyan)] whitespace-pre-wrap font-mono" data-testid="cmd-block">{children}</pre>
  );
}

function Callout({ tone = "info", icon: Icon, children }) {
  const palette = tone === "warn"
    ? { border: "rgba(255,176,32,0.45)", bg: "rgba(255,176,32,0.08)", color: "#ffb020" }
    : tone === "ok"
      ? { border: "rgba(0,255,157,0.45)", bg: "rgba(0,255,157,0.08)", color: "#00ff9d" }
      : { border: "rgba(0,229,255,0.45)", bg: "rgba(0,229,255,0.08)", color: "var(--neon-cyan)" };
  return (
    <div className="rounded-xl p-3 mt-3 flex items-start gap-2 text-sm"
      style={{ border: `1px solid ${palette.border}`, background: palette.bg, color: palette.color }}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0" />
      <div>{children}</div>
    </div>
  );
}

function TroubleRow({ issue, fix }) {
  return (
    <div className="py-2 border-b border-slate-800 last:border-b-0">
      <p className="text-white font-semibold text-sm">{issue}</p>
      <p className="text-slate-400 text-sm mt-1">{fix}</p>
    </div>
  );
}

function MiniBadge({ label, icon: Icon }) {
  return (
    <div className="rounded-xl px-3 py-3 border" style={{ borderColor: "rgba(120,150,220,0.18)", background: "rgba(8,11,22,0.6)" }}>
      <Icon className="w-4 h-4 mx-auto mb-1" style={{ color: "var(--neon-cyan)" }} />
      <p className="text-slate-300 text-xs font-bold uppercase tracking-widest">{label}</p>
    </div>
  );
}
